import { useEffect, useRef, useState } from 'react';
import LeadMagnetForm from './LeadMagnetForm';
import LeadMagnetModal from './LeadMagnetModal';
import { useLeadMagnetModal } from './useLeadMagnetModal';
import { CONTENT, IMAGE_ALT, PAGES_COUNT } from './leadMagnetContent';

export { PAGES_COUNT };

// ----------------------------------------------------------------------------
// Вариант full: форма встроена в страницу, картинка слева, контент и форма справа.
// Форма монтируется лениво, когда блок оказывается в зоне видимости.
// ----------------------------------------------------------------------------
function FullLeadMagnet() {
  const formRef = useRef<HTMLDivElement>(null);
  const [formMounted, setFormMounted] = useState(false);

  useEffect(() => {
    const el = formRef.current;
    if (!el) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setFormMounted(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setFormMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="posobie" className="py-16 md:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(512px,46%)_minmax(0,1fr)] gap-8 lg:gap-9">
            <div className="flex items-center justify-center p-0 sm:p-4 lg:p-4">
              <img
                src={CONTENT.full.image.mobile.src}
                alt={IMAGE_ALT}
                width={CONTENT.full.image.mobile.width}
                height={CONTENT.full.image.mobile.height}
                loading="lazy"
                className="w-full h-auto rounded-none sm:rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.12)] object-top lg:hidden"
              />
              <img
                src={CONTENT.full.image.desktop.src}
                alt={IMAGE_ALT}
                width={CONTENT.full.image.desktop.width}
                height={CONTENT.full.image.desktop.height}
                loading="lazy"
                className="hidden lg:block w-full h-auto object-top rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.12)] transition-transform duration-300 hover:scale-[1.02]"
              />
            </div>

            <div className="p-6 md:p-8 lg:py-10 lg:pr-10 lg:pl-0">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4 leading-snug">
                {CONTENT.full.heading}
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                {CONTENT.full.subtitle}
              </p>

              <p className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
                {CONTENT.full.listTitle}
              </p>
              <ul className="space-y-2.5 mb-6">
                {CONTENT.full.list.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-sm text-gray-700 leading-relaxed"
                  >
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-teal-600 text-base"></i>
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="text-sm text-gray-600 leading-relaxed mb-7">
                {CONTENT.full.afterList}
              </p>

              <div ref={formRef} className="w-full">
                {formMounted && <LeadMagnetForm />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Вариант compact: горизонтальная карточка, форма открывается в модальном окне.
// Модальное окно общее — то же самое использует хиро-тизер.
// ----------------------------------------------------------------------------
function CompactLeadMagnet() {
  const { open, openModal, closeModal, triggerRef } = useLeadMagnetModal();

  return (
    <section id="posobie" className="py-12 md:py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="bg-teal-50 border border-teal-100 rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row items-center gap-5 md:gap-6">
          <img
            src={CONTENT.compact.image.src}
            alt={IMAGE_ALT}
            width={CONTENT.compact.image.width}
            height={CONTENT.compact.image.height}
            loading="lazy"
            className="w-24 h-24 md:w-28 md:h-28 rounded-lg shadow-md object-cover object-top flex-shrink-0"
          />
          <p className="flex-1 text-sm md:text-base text-gray-700 leading-relaxed text-center sm:text-left">
            {CONTENT.compact.text}
          </p>
          <button
            ref={triggerRef}
            type="button"
            onClick={openModal}
            className="w-full sm:w-auto bg-teal-600 text-white px-6 py-3.5 rounded-lg text-sm md:text-base font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
          >
            {CONTENT.compact.button}
          </button>
        </div>
      </div>

      <LeadMagnetModal
        open={open}
        onClose={closeModal}
        title={CONTENT.compact.modalTitle}
      />
    </section>
  );
}

// ----------------------------------------------------------------------------
// Диспетчер: variant="full" (по умолчанию) — форма встроена в страницу,
// variant="compact" — компактная карточка с модальным окном.
// ----------------------------------------------------------------------------
export default function LeadMagnetBlock({
  variant = 'full',
}: {
  variant?: 'full' | 'compact';
}) {
  return variant === 'compact' ? <CompactLeadMagnet /> : <FullLeadMagnet />;
}